/**
 * The order of the screens between a signed-in merchant and the app.
 *
 * ONE total function over backend state — one read, `hooks/useEntryStatus`,
 * plus the sync-status query it seeds — so a reload at any point lands on
 * the same screen and the order is pinned by `entryScreen.test.js` rather
 * than by whichever `if` a later edit inserts first. `ProtectedLayout`
 * (App.jsx) renders whatever this names; the auth guards before it
 * (initialising, not signed in) are not part of the order — they decide
 * whether there is a merchant at all.
 *
 * The order, and why:
 *
 *   1. QUESTION — the sign-in claim left the one question only the merchant
 *      can answer: which of their organizations takes the new store, or
 *      whether they join someone else's. Before billing,
 *      because it is about a store that is not theirs yet — billing and sync
 *      are read for the store they are already in, which may be none — and
 *      the backend reads neither while the question is open.
 *   2. SETUP — the install setup step (`StoreSetup`): how the store's
 *      products are grouped, where its suppliers come from, and its planning
 *      defaults. Before billing, because the store's import starts from this
 *      answer and builds the catalog under these choices, and the paywall's
 *      price waits on that import. Owed once per install
 *      (`Store.setup_preferences_at`).
 *   3. PRICE_IMPORT — access denied (the entry read's billing part) and
 *      the import the price waits on is on its way, or failed
 *      (`utils/syncGate.priceWaitsOnImport`). The paywall names a price
 *      read off the store's order history, so a store whose history is
 *      still loading gets the price-wait screen first, and the paywall the moment
 *      the history is in. A failed import shows here too: the paywall's
 *      pricing read would start it again on every load.
 *   4. PAYWALL — access denied, or the billing read failed.
 *   5. APP — once the entry read's sync part is in. The import itself
 *      never stands in front of the app: the sidebar's `SyncProgressPill`
 *      shows it, and the pill, the Home tiles and the Forecast link read the
 *      payload that part seeded.
 *
 * Each part fails CLOSED into its own retry screen: a claim read that failed
 * may hide a question, a setup read that failed may hide the step, a billing
 * read that failed may hide a denial, and a sync read that failed reads like
 * "nothing imported" — any of them would let a merchant into a store in a
 * state nobody can vouch for.
 */

export const ENTRY_SCREEN = {
  /** A backend read still pending. */
  LOADING: 'loading',
  /** The claim read, or the entry read as a whole, failed; retry, never the app. */
  CLAIM_ERROR: 'claim-error',
  /** The one question (`StoreClaimQuestion`). */
  QUESTION: 'question',
  /** The setup read failed; retry, never the app. */
  SETUP_ERROR: 'setup-error',
  /** The install setup step (`StoreSetup`). */
  SETUP: 'setup',
  /** Access denied and the price waits on the import (`SyncScreen`). */
  PRICE_IMPORT: 'price-import',
  /** Access denied, or the billing read failed (`PricingGate`). */
  PAYWALL: 'paywall',
  /** The sync read failed; retry, never the app. */
  SYNC_ERROR: 'sync-error',
  /** Every screen passed. */
  APP: 'app',
};

/** A sync part the entry read could not read: the sync read failed. */
const SYNC_READ_FAILED = { isLoading: false, isError: true, priceWaitsOnImport: false };

/**
 * @param {Object} input
 * @param {{checked: boolean, claim: {failed: boolean, question: object|null},
 *   setup: {failed: boolean, step: object|null}|null,
 *   billing: {status: object|null}|null, sync: {failed: boolean}|null}} input.entry
 *   `useEntryStatus`'s view model. `setup` / `billing` / `sync` are null
 *   where they do not apply (no Shopify store selected).
 * @param {{isLoading: boolean, isError: boolean, priceWaitsOnImport: boolean}} input.sync
 *   `useSyncStatus`'s view model; read only where the entry read served the
 *   sync part.
 * @returns {string} an ENTRY_SCREEN value
 */
export function entryScreen({ entry, sync }) {
  if (!entry.checked) return ENTRY_SCREEN.LOADING;
  if (entry.claim.failed) return ENTRY_SCREEN.CLAIM_ERROR;
  if (entry.claim.question) return ENTRY_SCREEN.QUESTION;

  if (entry.setup) {
    if (entry.setup.failed) return ENTRY_SCREEN.SETUP_ERROR;
    if (entry.setup.step) return ENTRY_SCREEN.SETUP;
  }

  const importRead = entry.sync && (entry.sync.failed ? SYNC_READ_FAILED : sync);

  const { billing } = entry;
  if (billing) {
    // `status` null is a failed read: denied, with a retry card and no
    // Subscribe button (`PricingGate`).
    if (!billing.status) return ENTRY_SCREEN.PAYWALL;
    // `needs_subscription`, never `needs_resubscribe`: that one answers "has
    // this shop ever had a paid subscription", which is true of every paying
    // merchant — routing on it would paywall the whole customer base.
    // PricingGate reads it, to pick its heading. A pending tier change with
    // paid_until still ahead doesn't gate the app; the app-wide banner states it.
    if (billing.status.needs_subscription) {
      if (importRead) {
        if (importRead.isLoading) return ENTRY_SCREEN.LOADING;
        if (importRead.isError) return ENTRY_SCREEN.SYNC_ERROR;
        if (importRead.priceWaitsOnImport) return ENTRY_SCREEN.PRICE_IMPORT;
      }
      return ENTRY_SCREEN.PAYWALL;
    }
  }

  // Inside the app only the entry read's own sync part gates: a later poll
  // that fails is the pill's to say, and must not take the open tabs away.
  if (entry.sync?.failed) return ENTRY_SCREEN.SYNC_ERROR;
  return ENTRY_SCREEN.APP;
}

export default entryScreen;
